'use client';
import React from 'react';

import { BlockWrapper } from '../../../../components/block-wrapper/block-wrapper';
import { RenderBlocks } from '../../../../components/blocks/render-blocks';
import { Breadcrumbs } from '../../../../components/breadcrumbs';
import { PageComponentMain } from '../../../../components/page-components/main/page-component-main';
import useGetMobileAppWebViewHeader from '../../../../hooks/use-get-mobile-app-web-view';
import { IMeetingplacePageHomeMinified } from '@/lib/fragments/meetingPlacePageHome-minified';
import { ILibraryMeetingPlaceMinifiedCoordinates } from '@/lib/fragments/libraryMeetingPlace';
import { IMeetingPlacePageFindUs } from '@/lib/fragments/meetingPlacePageFindUs';
import { GtmPageView } from '@/components/page-components/analytics/gtm-page-view';
import { useSendGtmEvent } from '@/hooks/use-send-gtm-event';
import { useUser } from '@/providers/UserProvider';
import { useIsMPLivliFamily } from '@/providers/ConfigProvider';
interface MeetingPlaceFindUsHomePageExtended extends IMeetingplacePageHomeMinified {
  meetingPlace: ILibraryMeetingPlaceMinifiedCoordinates;
}

interface MeetingPlacePageFindUsProps {
  metaData: any;
  node_locale: string;
  preview: boolean;
  findUsData: {
    meetingPlacePageFindUs: IMeetingPlacePageFindUs & {
      mainBlockCollection?: {
        items: any[];
      };
      blocksCollection?: {
        items: any[];
      };
    };
    meetingPlacePageHomeCollection: {
      items: MeetingPlaceFindUsHomePageExtended[];
    };
  } | null;
}

const MeetingPlacePageFindUs = ({
  metaData,
  node_locale,
  preview,
  findUsData,
}: MeetingPlacePageFindUsProps) => {
  const { user } = useUser();
  const isLivliFamily = useIsMPLivliFamily();
  const page = findUsData?.meetingPlacePageFindUs;
  const homepage = findUsData?.meetingPlacePageHomeCollection?.items[0];
  const mapsApiKey =
    process.env.GOOGLE_MAPS_API_KEY ||
    process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;

  const isWebView = useGetMobileAppWebViewHeader();

  useSendGtmEvent({
    event: 'view_directions',
    user_status: isLivliFamily ? (user ? 'logged_in' : 'guest') : undefined,
  });
  return (
    <>
      {/* ✅ GTM PAGE VIEW */}
      <GtmPageView
        homepage={homepage}
        language={node_locale}
        pageTitle={page?.name ?? ''}
      />
      <main className="bg-background-primary-light px-6 lg:-ml-22 lg:bg-transparent lg:px-0">
        <Breadcrumbs
          breadcrumbs={[page?.name || '']}
          homeText={homepage?.breadcrumbHomeText || ''}
          cssClass={`lg:ml-22 ${isWebView ? '!pt-8' : ''}`}
          node_locale={node_locale}
        />
        <div className="grid grid-cols-11 gap-6 pt-5 lg:gap-5">
          <div className="col-span-11 lg:col-span-5">
            <div className="aspect-0.75/1 w-full transform-gpu overflow-hidden rounded-xl lg:rounded-xxl">
              <iframe
                className="aspect-0.75/1 w-full"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps/embed/v1/place?key=${mapsApiKey}&q=${homepage?.meetingPlace?.coordinates?.lat},${homepage?.meetingPlace?.coordinates?.lon}&zoom=12`}
              ></iframe>
            </div>
          </div>
          <div className="col-span-11 lg:col-span-6">
            <PageComponentMain
              header={page?.name || ''}
              blocks={page?.mainBlockCollection?.items || page?.mainBlock || []}
              richText={page?.body}
              node_locale={page?.sys?.locale || node_locale}
              showSocialShare={homepage?.meetingPlace?.showSocialShare || false}
            />
            {page?.blocksCollection && (
              <div className="-mx-6 bg-brand-white pt-16 lg:-mx-0 lg:mb-5 lg:pt-0">
                {page?.blocksCollection?.items.map((block: any) => (
                  <BlockWrapper
                    key={block?.sys?.id}
                    className="mb-6 rounded-card-mobile px-6 py-11 last:mb-0 lg:p-20 xl:p-24"
                  >
                    <RenderBlocks blocks={[block]} />
                  </BlockWrapper>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
};
export default MeetingPlacePageFindUs;
